import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from "prop-types";
import {useSelector, useDispatch} from "react-redux";
import TableViewHook from "../../containers/TableViewHook";
import TableGridHook from "../../containers/TableGridHook";
import {getTemplateList, getTemplateReferences, updatePageAndGetList, updateSortAndGetList} from "../../actions/templateListActions";
import GroupCreateEditDrawer, {groupCreateEditDrawerProps} from "../drawers/GroupCreateEditDrawerHook";
import ReviewDrawer, {reviewDrawerProps} from "../drawers/ReviewDrawerHook";
import ReferencesDrawerHook, {referencesDrawerProps} from "../drawers/ReferencesDrawerHook";
import {openDrawer} from "../../actions/drawerActions";
import FilterPopOverHook from "../dialogs/filters/FilterPopOverHook";
import * as metadataTemplateTypes from "../../constants/metadataTemplateTypes";
import ArgoTemplateListCreateFabHook from "../common/ArgoTemplateListCreateFabHook";
import formatCopyText from "../../utilities/formatCopyText";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {QD_URL} from "../../constants/systemConstants";
import ArgoListTitleAndLoadingAnimationHook from "../common/ArgoListTitleAndLoadingAnimationHook";
import * as TEMPLATE from "../../constants/templateConstants";
import * as SYSTEM from "../../constants/systemConstants";
import ArgoScreenSizePopUpHook from "../common/ArgoScreenSizePopUpHook";

const GroupList = ({ match, location }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(false);
    const [closePopUp, setClosePopUp] = useState(false);
    
    const groupTemplateList = useSelector(state => state.groupTemplateList);
    const filterState = useSelector(state => state.filter[TEMPLATE.FILTER.current.group] || {});

    useEffect(() => {
        setLoading(true);
        dispatch(getTemplateList(filterState, TEMPLATE.TYPE.GROUP, TEMPLATE.MODE.CURRENT));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        // Called twice, first pass set filter false, then on second call list is loaded and loading animation can be stopped
        if (filter) {
            setFilter(false);
        }
        else if (loading) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            }
            else {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupTemplateList, filterState]);

    // Used to test loading animation rendering
    const delayedSetLoadingFalse = () => {
        setLoading(false);
    };

    const setLoadingStateTrue = useCallback((callback, parm1 = null) => {
        // If a parameter is passed then pass it to call back, should be the template of the item to be saved edit or created
        setLoading(true);
        setFilter(false);
        if (parm1 === null) {
            callback();
        } else {
            callback(parm1);
        }
    }, []);

    const openEdit = useCallback((templateKey, id, templateMode) => {
        if (!templateKey && match && match.params) {
            templateKey = match.params.templateKey;
        }

        dispatch(openDrawer(groupCreateEditDrawerProps, <GroupCreateEditDrawer templateKey={templateKey} templateId={id} filter={filterState} templateMode={templateMode} setLoadingState={setLoadingStateTrue}/>));
    }, [dispatch, match, filterState, setLoadingStateTrue]);

    const openCreate = useCallback(() => {
        dispatch(openDrawer(groupCreateEditDrawerProps, <GroupCreateEditDrawer createMode={true} filter={filterState} setLoadingState={setLoadingStateTrue}/>));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, setLoadingStateTrue]);

    const openGroupReviewDrawer = useCallback((templateKey, id, name, row, event) => {
        if (!templateKey && match && match.params) {
            templateKey = match.params.templateKey;
        }
        formatCopyText(row, event, "Group:");
        dispatch(openDrawer(reviewDrawerProps, <ReviewDrawer templateKey={templateKey} templateId={id} templateType={TEMPLATE.TYPE.GROUP}/>));
    }, [dispatch, match]);

    const viewReferences = useCallback((templateKey, id, p1, p2, row) => {
        dispatch(getTemplateReferences(TEMPLATE.TYPE.GROUP, id));
        dispatch(openDrawer(referencesDrawerProps, <ReferencesDrawerHook templateId={id} templateType={TEMPLATE.TYPE.GROUP} row={row} title="Group:"/>));
    }, [dispatch]);

    const onPageChange = useCallback((page, size, event) => {
        setLoading(true);
        setFilter(true);
        dispatch(updatePageAndGetList(page, size, filterState, TEMPLATE.TYPE.GROUP, TEMPLATE.MODE.CURRENT, event));
    }, [dispatch, filterState]);

    const onSortChange = useCallback((sort, direction) => {
        setLoading(true);
        setFilter(true);
        dispatch(updateSortAndGetList(sort, direction, filterState, TEMPLATE.TYPE.GROUP, TEMPLATE.MODE.CURRENT));
    }, [dispatch, filterState]);

    const handleClosePopUp = () => {
        localStorage.setItem("hasSeenWelcomeMessage", "true");
        setClosePopUp(true);
    };

    // const {
    //     groupTemplateList
    // } = this.props;

    const customActions = [
        {
            func: openGroupReviewDrawer,
            label: 'Review'
        },
        {
            func: openEdit,
            label: "Edit"
        },
        {
            func: viewReferences,
            label: "References"
        }
    ];

    // Used by the versions list and stored in the version filters
    let defaultActions = {
        edit: openEdit,
        create: openCreate,
        review: openGroupReviewDrawer
    };

    return (
        <div id="GroupList-container-div" style={{height: "100%", padding: "15px", marginTop: '64px'}}>
            {closePopUp ? "" : <ArgoScreenSizePopUpHook closeFunction={handleClosePopUp}/>}

            <ArgoListTitleAndLoadingAnimationHook fileName="GroupList" listTitle="Groups" loading={loading} showOutstandingAlert={false} filter={filter}/>

            <div style={{clear: "both"}}></div>

            <Card id="GroupList-card" variant="outlined" sx={{border: "1px solid lightgray", height: "100%", boxShadow: "2px 4px whitesmoke"}}>

                <CardContent id="GroupList-card-content">

                    <TableViewHook
                        id="GroupList-table-view"
                        templateType={TEMPLATE.TYPE.GROUP}
                        templateMode={TEMPLATE.MODE.CURRENT}
                        setLoadingState={setLoadingStateTrue}
                        filter={<FilterPopOverHook page="groups"
                                               templateType={TEMPLATE.TYPE.GROUP}
                                               templateMode={TEMPLATE.MODE.CURRENT}
                                               filterName={TEMPLATE.FILTER.current.group}
                                               submitValue={getTemplateList}
                                               statuses={["All", "Draft", "Published"]}
                                               setLoadingState={setLoadingStateTrue} parentName={"GroupList"}
                        />}
                    >

                        <TableGridHook
                            id="GroupList-table-grid"
                            displayType={"groups"}
                            dataSource={groupTemplateList}
                            nameDrawer={openGroupReviewDrawer}
                            link={QD_URL +"/group-editor/"}
                            customActions={customActions}
                            pageState = {filterState}
                            onPageChange = {onPageChange}
                            sortState = {filterState}
                            onSortChange = {onSortChange}
                            type={metadataTemplateTypes.GroupTemplate}
                            showGroupType={true}
                            templateType={TEMPLATE.TYPE.GROUP}
                            templateMode={TEMPLATE.MODE.CURRENT}
                            defaultActions={defaultActions}
                            showReferencesAndVersionsSameColumn={true}
                        />
                    </TableViewHook>

                    <ArgoTemplateListCreateFabHook
                        fileName="GroupList"
                        fieldName="fab"
                        hoverText="Create Group"
                        onClick={openCreate}
                    />

                </CardContent>

            </Card>

        </div>
    );
};

GroupList.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,
};

export default GroupList;
